import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import {
    Button,
    Chip,
    Label,
    ListBox,
    SearchField,
    Select,
} from '@heroui/react';
import type { LocaleCode, RecordableOperation } from '@/api/models';
import { PATH } from '@/router/PATH';
import { OperationCategoryChip } from '../../../components';
import { isoToDisplayDate } from '@/pages/accounting/statements/statementMeta';
import { cn } from '@/utils/cnUtils';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    operationAltName,
    operationName,
    reasonCopy,
    rowElementId,
    SEARCH_INPUT_ID,
    type PickerGroups,
    type PickerSort,
} from './commandPickerMeta';

type CommandPickerViewProps = {
    locale: LocaleCode;
    query: string;
    onQueryChange: (query: string) => void;
    onSearchKeyDown: (event: React.KeyboardEvent) => void;
    sort: PickerSort;
    onSortChange: (sort: PickerSort) => void;
    groups: PickerGroups;
    shortcuts: RecordableOperation[];
    highlight: number;
    onHighlight: (index: number) => void;
    onSelect: (key: string) => void;
    /** Whether to offer a link to the Templates tab from the dead-end footer. */
    canEditTemplates: boolean;
};

const LIST_ID = 'operations-record-picker-list';

/**
 * Which operation is being recorded — render only.
 *
 * **A search field with a ranked list under it, not a grid of cards.** The grid
 * worked at nine operations and stops working the moment a tenant configures
 * their own: every card is the same size and says the same amount, so finding
 * one becomes reading all of them. Here the handful somebody uses daily are two
 * keystrokes away and the rest are one query away.
 *
 * **The list is a listbox the search field drives.** The caret never leaves the
 * input — that is what makes typing-then-choosing one gesture — so the rows
 * cannot be HeroUI's `ListBox`, which owns its own roving focus and would fight
 * the input for it. They are `role="option"` elements with `aria-activedescendant`
 * on the input, which is the ARIA pattern for exactly this shape. Everything
 * around the list is a HeroUI component.
 *
 * Each row carries a **category chip**, quieter than anything actionable on the
 * screen: it says what kind of operation this is, which is how a sixty-row list
 * is scanned rather than read. The layout leaves room for it to become a filter
 * later; it is not one yet.
 *
 * **Unavailable rows are below every selectable one, always.** They exist so an
 * operation you searched for and cannot use explains itself rather than looking
 * deleted; ranking one above something you *can* record would be sorting by how
 * well you typed instead of by what you can do next.
 */
export const CommandPickerView: React.FC<CommandPickerViewProps> = ({
    locale,
    query,
    onQueryChange,
    onSearchKeyDown,
    sort,
    onSortChange,
    groups,
    shortcuts,
    highlight,
    onHighlight,
    onSelect,
    canEditTemplates,
}) => {
    const { t } = useTranslation('pages');

    return (
        <section
            aria-label={t('operations.record.picker.title')}
            className="flex flex-col gap-3 rounded-2xl border border-separator bg-surface p-4"
            {...getNodeTestIdProp('operations-record-picker')}
        >
            <span className="text-sm font-medium text-foreground">
                {t('operations.record.picker.step')}
            </span>

            <SearchField
                aria-controls={LIST_ID}
                fullWidth
                onChange={onQueryChange}
                onKeyDown={onSearchKeyDown}
                value={query}
                variant="secondary"
                {...getNodeTestIdProp('operations-record-picker-search')}
            >
                <Label className="sr-only">
                    {t('operations.record.picker.searchLabel')}
                </Label>
                <SearchField.Group>
                    <SearchField.SearchIcon />
                    <SearchField.Input
                        aria-activedescendant={optionId(
                            groups.selectable[highlight]?.key,
                        )}
                        id={SEARCH_INPUT_ID}
                        placeholder={t(
                            'operations.record.picker.searchPlaceholder',
                        )}
                    />
                    <SearchField.ClearButton />
                </SearchField.Group>
            </SearchField>

            <div className="flex flex-wrap items-center justify-between gap-2">
                <span
                    className="text-xs text-muted tabular-nums"
                    {...getNodeTestIdProp('operations-record-picker-counter')}
                >
                    {t('operations.record.picker.counter', {
                        shown: groups.shown,
                        total: groups.total,
                    })}
                </span>
            </div>

            {shortcuts.length > 0 ? (
                <div
                    className="flex flex-col gap-2"
                    {...getNodeTestIdProp('operations-record-picker-shortcuts')}
                >
                    <span className="text-xs text-muted">
                        {t('operations.record.picker.frequent')}
                    </span>
                    <div className="flex flex-wrap gap-2">
                        {shortcuts.map((operation) => (
                            <Button
                                key={operation.key}
                                onPress={() => onSelect(operation.key)}
                                size="sm"
                                variant="outline"
                                {...getNodeTestIdProp(
                                    `operations-record-picker-shortcut-${operation.key}`,
                                )}
                            >
                                {operationName(operation, locale)}
                                <Chip size="sm" variant="soft">
                                    {t('operations.record.picker.uses', {
                                        count: operation.recordCount,
                                    })}
                                </Chip>
                            </Button>
                        ))}
                    </div>
                </div>
            ) : null}

            <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-xs text-muted">
                    {t('operations.record.picker.allOperations', {
                        count: groups.total,
                    })}
                </span>
                <Select
                    aria-label={t('operations.record.picker.sortLabel')}
                    className="w-40"
                    onChange={(next) =>
                        next && onSortChange(next as PickerSort)
                    }
                    value={sort}
                >
                    <Select.Trigger
                        {...getNodeTestIdProp('operations-record-picker-sort')}
                    >
                        <Select.Value />
                        <Select.Indicator />
                    </Select.Trigger>
                    <Select.Popover>
                        <ListBox>
                            <ListBox.Item
                                id="used"
                                textValue={t(
                                    'operations.record.picker.sortUsed',
                                )}
                            >
                                {t('operations.record.picker.sortUsed')}
                                <ListBox.ItemIndicator />
                            </ListBox.Item>
                            <ListBox.Item
                                id="name"
                                textValue={t(
                                    'operations.record.picker.sortName',
                                )}
                            >
                                {t('operations.record.picker.sortName')}
                                <ListBox.ItemIndicator />
                            </ListBox.Item>
                        </ListBox>
                    </Select.Popover>
                </Select>
            </div>

            <ul
                aria-label={t('operations.record.picker.title')}
                className="flex max-h-96 flex-col gap-1 overflow-y-auto"
                id={LIST_ID}
                role="listbox"
                {...getNodeTestIdProp('operations-record-picker-rows')}
            >
                {groups.best ? (
                    <GroupHeading
                        label={t('operations.record.picker.bestMatch')}
                    />
                ) : null}
                {groups.best ? (
                    <Row
                        highlighted={highlight === 0}
                        locale={locale}
                        onHighlight={() => onHighlight(0)}
                        onSelect={onSelect}
                        operation={groups.best}
                    />
                ) : null}

                {groups.best && groups.others.length > 0 ? (
                    <GroupHeading
                        label={t('operations.record.picker.otherMatches')}
                    />
                ) : null}
                {groups.others.map((operation, index) => {
                    const position = groups.best ? index + 1 : index;
                    return (
                        <Row
                            highlighted={highlight === position}
                            key={operation.key}
                            locale={locale}
                            onHighlight={() => onHighlight(position)}
                            onSelect={onSelect}
                            operation={operation}
                        />
                    );
                })}

                {groups.unavailable.length > 0 ? (
                    <GroupHeading
                        label={t('operations.record.picker.unavailable')}
                    />
                ) : null}
                {groups.unavailable.map((operation) => (
                    <UnavailableRow
                        key={operation.key}
                        locale={locale}
                        operation={operation}
                    />
                ))}

                {groups.shown === 0 ? (
                    <li
                        className="px-1 py-4 text-sm text-muted"
                        {...getNodeTestIdProp(
                            'operations-record-picker-no-match',
                        )}
                    >
                        {t('operations.record.picker.noMatch', { query })}
                    </li>
                ) : null}
            </ul>

            <p
                className="flex flex-wrap items-center gap-1 text-xs text-muted"
                {...getNodeTestIdProp('operations-record-picker-footer')}
            >
                {t('operations.record.picker.deadEnd')}
                {canEditTemplates ? (
                    <Link
                        className="text-accent underline-offset-2 hover:underline"
                        to={PATH.ACCOUNTING.OPERATIONS.TEMPLATES}
                        {...getNodeTestIdProp(
                            'operations-record-picker-templates-link',
                        )}
                    >
                        {t('operations.record.picker.openTemplates')}
                    </Link>
                ) : null}
            </p>
        </section>
    );
};

/** A stable DOM id per row, so `aria-activedescendant` has something to point at. */
const optionId = (key: string | undefined): string | undefined =>
    key ? rowElementId(key) : undefined;

const GroupHeading: React.FC<{ label: string }> = ({ label }) => (
    <li
        aria-hidden
        className="px-1 pt-2 text-xs text-muted"
        role="presentation"
    >
        {label}
    </li>
);

type RowProps = {
    operation: RecordableOperation;
    locale: LocaleCode;
    highlighted: boolean;
    onHighlight: () => void;
    onSelect: (key: string) => void;
};

/**
 * One choosable row.
 *
 * Hovering moves the highlight so the mouse and the keyboard agree about which
 * row Enter would take — two separate "current" rows on one list is how a user
 * ends up posting the wrong operation.
 */
const Row: React.FC<RowProps> = ({
    operation,
    locale,
    highlighted,
    onHighlight,
    onSelect,
}) => {
    const { t } = useTranslation('pages');

    return (
        <li
            aria-selected={highlighted}
            className={cn(
                'flex cursor-[var(--cursor-interactive)] items-center gap-3 rounded-xl px-3 py-2',
                highlighted && 'bg-surface-secondary',
            )}
            id={optionId(operation.key)}
            onClick={() => onSelect(operation.key)}
            onMouseMove={onHighlight}
            role="option"
            {...getNodeTestIdProp(
                `operations-record-picker-option-${operation.key}`,
            )}
        >
            <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-sm font-medium text-foreground">
                    {operationName(operation, locale)}
                </span>
                <span className="truncate text-xs text-muted">
                    {operationAltName(operation, locale)}
                </span>
            </span>
            <OperationCategoryChip
                category={operation.category}
                locale={locale}
                slot={operation.key}
            />
            {operation.recordCount > 0 ? (
                <span className="text-xs text-muted tabular-nums">
                    {t('operations.record.picker.uses', {
                        count: operation.recordCount,
                    })}
                </span>
            ) : null}
        </li>
    );
};

/**
 * A row that matched and cannot be chosen.
 *
 * It carries its reason inline rather than a tooltip: the whole point of showing
 * it is that the user went looking for this operation, and an explanation they
 * have to hover for is one they will not read.
 */
const UnavailableRow: React.FC<{
    operation: RecordableOperation;
    locale: LocaleCode;
}> = ({ operation, locale }) => {
    const { t } = useTranslation('pages');
    const copy = reasonCopy(operation, isoToDisplayDate);

    return (
        <li
            aria-disabled
            className="flex flex-col gap-0.5 rounded-xl px-3 py-2 opacity-60"
            id={optionId(operation.key)}
            role="option"
            {...getNodeTestIdProp(
                `operations-record-picker-unavailable-${operation.key}`,
            )}
        >
            <span className="truncate text-sm font-medium text-foreground">
                {operationName(operation, locale)}
            </span>
            <span className="flex items-start gap-1 text-xs text-warning">
                <Icon
                    aria-hidden
                    className="mt-0.5 shrink-0"
                    icon="gravity-ui:circle-info"
                />
                {t(copy.key, copy.args)}
            </span>
        </li>
    );
};
